import React, { useState } from 'react';
import { ArrowLeftIcon, EditIcon, DownloadIcon, FileTextIcon, UserIcon, CalendarIcon, DollarSignIcon, MinusSquareIcon, CheckCircleIcon, AlertCircleIcon, LinkIcon, ExternalLinkIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { NoteVisibility } from '../common/StandardizedNotes';
import { GlobeIcon, UsersIcon, LockIcon, MessageSquareIcon } from 'lucide-react';
export function CreditNoteDetail({
  creditNote,
  onBack,
  onEdit,
  locale = 'en-US'
}) {
  // Function to determine badge color based on status
  const getStatusBadgeClass = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Posted':
        return 'bg-blue-100 text-blue-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Added state for related communications
  const [relatedCommunications, setRelatedCommunications] = useState([{
    id: 4,
    title: 'Office Supplies Return - Credit Note Clarification',
    timestamp: '2023-10-19T16:20:00Z',
    messageCount: 1,
    lastMessage: "Credit note received for returned items from last month's order. Quality issues were reported to the vendor."
  }]);
  // Function to render note visibility icon and label
  const renderNoteVisibility = () => {
    if (!creditNote.noteVisibility) return null;
    const getVisibilityIcon = () => {
      switch (creditNote.noteVisibility) {
        case 'Public':
          return <GlobeIcon className="h-4 w-4 text-green-500" data-id="element-3204" />;
        case 'Internal':
          return <UsersIcon className="h-4 w-4 text-blue-500" data-id="element-3205" />;
        case 'Role-based':
          return <UsersIcon className="h-4 w-4 text-purple-500" data-id="element-3206" />;
        case 'User-specific':
          return <UserIcon className="h-4 w-4 text-orange-500" data-id="element-3207" />;
        case 'Creator-only':
          return <LockIcon className="h-4 w-4 text-red-500" data-id="element-3208" />;
        default:
          return null;
      }
    };
    const getVisibilityLabel = () => {
      switch (creditNote.noteVisibility) {
        case 'Public':
          return 'Public - Visible to everyone';
        case 'Internal':
          return 'Internal - Visible to all internal users';
        case 'Role-based':
          return `Role-based - Visible to specific roles: ${creditNote.noteVisibilityDetails || 'None specified'}`;
        case 'User-specific':
          return `User-specific - Visible to specific users: ${creditNote.noteVisibilityDetails || 'None specified'}`;
        case 'Creator-only':
          return 'Private - Visible only to creator';
        default:
          return '';
      }
    };
    return <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-3209">
        {getVisibilityIcon()}
        <span className="ml-1" data-id="element-3210">{getVisibilityLabel()}</span>
      </div>;
  };
  // Group credit note lines by invoice
  const linesByInvoice = {};
  if (creditNote.lines) {
    creditNote.lines.forEach(line => {
      if (line.linkedInvoiceId) {
        if (!linesByInvoice[line.linkedInvoiceId]) {
          linesByInvoice[line.linkedInvoiceId] = [];
        }
        linesByInvoice[line.linkedInvoiceId].push(line);
      }
    });
  }
  // Get invoice details by ID
  const getInvoiceById = invoiceId => {
    return creditNote.linkedInvoices.find(inv => inv.id === invoiceId) || null;
  };
  return <div className="bg-white shadow-lg rounded-lg overflow-hidden" data-id="element-3211">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center" data-id="element-3212">
        <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-3213">
          <MinusSquareIcon className="h-5 w-5 mr-2 text-green-600" data-id="element-3214" />
          Credit Note Details
        </h3>
        <div className="flex space-x-2" data-id="element-3215">
          <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3216">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-3217" />
            Back to List
          </button>
          {(creditNote.status === 'Draft' || creditNote.status === 'Rejected') && <button onClick={() => onEdit(creditNote)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3218">
              <EditIcon className="h-4 w-4 mr-1" data-id="element-3219" />
              Edit
            </button>}
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3220">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-3221" />
            Download PDF
          </button>
        </div>
      </div>
      <div className="p-6" data-id="element-3222">
        {/* Header Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-3223">
          <div data-id="element-3224">
            <div className="flex items-center mb-4" data-id="element-3225">
              <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3226" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3227">
                Credit Note Information
              </h4>
            </div>
            <div className="bg-gray-50 rounded-md p-4 space-y-3" data-id="element-3228">
              <div className="flex justify-between" data-id="element-3229">
                <span className="text-sm font-medium text-gray-500" data-id="element-3230">
                  Credit Note Number:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3231">
                  {creditNote.creditNoteNumber}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3232">
                <span className="text-sm font-medium text-gray-500" data-id="element-3233">
                  Status:
                </span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(creditNote.status)}`} data-id="element-3234">
                  {creditNote.status}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3235">
                <span className="text-sm font-medium text-gray-500" data-id="element-3236">Date:</span>
                <span className="text-sm text-gray-900" data-id="element-3237">
                  {new Date(creditNote.creditNoteDate).toLocaleDateString(locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3238">
                <span className="text-sm font-medium text-gray-500" data-id="element-3239">
                  Reference:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3240">
                  {creditNote.reference || 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <div data-id="element-3241">
            <div className="flex items-center mb-4" data-id="element-3242">
              <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3243" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3244">
                Vendor Information
              </h4>
            </div>
            <div className="bg-gray-50 rounded-md p-4 space-y-3" data-id="element-3245">
              <div className="flex justify-between" data-id="element-3246">
                <span className="text-sm font-medium text-gray-500" data-id="element-3247">
                  Vendor Name:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3248">
                  {creditNote.vendor}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3249">
                <span className="text-sm font-medium text-gray-500" data-id="element-3250">
                  Currency:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3251">
                  {creditNote.currency}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3252">
                <span className="text-sm font-medium text-gray-500" data-id="element-3253">
                  Total Amount:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3254">
                  {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3255">
                <span className="text-sm font-medium text-gray-500" data-id="element-3256">
                  Applied Amount:
                </span>
                <div className="flex items-center" data-id="element-3257">
                  {creditNote.appliedAmount > 0 ? creditNote.unappliedAmount > 0 ? <AlertCircleIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-3258" /> : <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3259" /> : <AlertCircleIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3260" />}
                  <span className="text-sm text-gray-900" data-id="element-3261">
                    {formatCurrency(creditNote.appliedAmount || 0, creditNote.currency, locale)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between" data-id="element-3262">
                <span className="text-sm font-medium text-gray-500" data-id="element-3263">
                  Unapplied Amount:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3264">
                  {formatCurrency(creditNote.unappliedAmount || 0, creditNote.currency, locale)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Line Items */}
        <div className="mt-6" data-id="element-3265">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-3266">Line Items</h3>
          <div className="mt-4 overflow-x-auto" data-id="element-3267">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3268">
              <thead className="bg-gray-50" data-id="element-3269">
                <tr data-id="element-3270">
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3271">
                    Description
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3272">
                    Quantity
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3273">
                    Unit Price
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3274">
                    Tax Code
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3275">
                    Tax Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3276">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3277">
                {creditNote.lines.map((line, index) => <tr key={index} data-id="element-3278">
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-3279">
                      {line.description}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3280">
                      {line.quantity}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3281">
                      {formatCurrency(line.unitPrice, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3282">
                      {line.taxCode} ({line.taxRate}%)
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3283">
                      {formatCurrency(line.taxAmount, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3284">
                      {formatCurrency(line.amount, creditNote.currency, locale)}
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-50" data-id="element-3285">
                <tr data-id="element-3286">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-500" data-id="element-3287">
                    Subtotal:
                  </td>
                  <td colSpan={2} className="px-3 py-2 text-sm text-gray-900" data-id="element-3288">
                    {formatCurrency(creditNote.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0), creditNote.currency, locale)}
                  </td>
                </tr>
                <tr data-id="element-3289">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-500" data-id="element-3290">
                    Tax Total:
                  </td>
                  <td colSpan={2} className="px-3 py-2 text-sm text-gray-900" data-id="element-3291">
                    {formatCurrency(creditNote.lines.reduce((sum, line) => sum + (line.taxAmount || 0), 0), creditNote.currency, locale)}
                  </td>
                </tr>
                <tr data-id="element-3292">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-900" data-id="element-3293">
                    Total:
                  </td>
                  <td colSpan={2} className="px-3 py-2 text-sm font-bold text-gray-900" data-id="element-3294">
                    {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        {/* Linked Invoices */}
        <div className="mb-8" data-id="element-3295">
          <div className="flex items-center mb-4" data-id="element-3296">
            <LinkIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3297" />
            <h4 className="text-lg font-medium text-gray-900" data-id="element-3298">
              Linked Invoices
            </h4>
          </div>
          {creditNote.linkedInvoices && creditNote.linkedInvoices.length > 0 ? <div data-id="element-3299">
              {creditNote.linkedInvoices.map(invoice => <div key={invoice.id} className="mb-4 border border-gray-200 rounded-md overflow-hidden" data-id="element-3300">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-3301">
                    <div data-id="element-3302">
                      <h5 className="text-sm font-medium text-gray-900" data-id="element-3303">
                        Invoice: {invoice.invoiceNumber}
                      </h5>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-3304">
                        Date:{' '}
                        {new Date(invoice.invoiceDate).toLocaleDateString(locale)}{' '}
                        | Amount:{' '}
                        {formatCurrency(invoice.amount, invoice.currency, locale)}
                      </div>
                    </div>
                    <button className="text-indigo-600 hover:text-indigo-900 text-sm flex items-center" data-id="element-3305">
                      <ExternalLinkIcon className="h-4 w-4 mr-1" data-id="element-3306" />
                      View Invoice
                    </button>
                  </div>
                  {/* Show credit note lines linked to this invoice */}
                  {linesByInvoice[invoice.id] && linesByInvoice[invoice.id].length > 0 ? <div className="p-4" data-id="element-3307">
                      <h6 className="text-xs font-medium text-gray-500 mb-2" data-id="element-3308">
                        Credit Note Lines Applied to This Invoice:
                      </h6>
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-3309">
                        <thead className="bg-gray-50" data-id="element-3310">
                          <tr data-id="element-3311">
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3312">
                              Credit Line Description
                            </th>
                            <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3313">
                              Amount
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3314">
                              Applied To
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-3315">
                          {linesByInvoice[invoice.id].map((line, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-3316">
                              <td className="px-3 py-2 text-sm text-gray-900" data-id="element-3317">
                                {line.description}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right" data-id="element-3318">
                                {formatCurrency(line.quantity * line.unitPrice, creditNote.currency, locale)}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-500" data-id="element-3319">
                                {line.linkedLineDescription || 'Entire invoice'}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div> : <div className="p-4 text-sm text-gray-500" data-id="element-3320">
                      No credit note lines specifically linked to this invoice.
                    </div>}
                </div>)}
            </div> : <div className="bg-gray-50 p-4 text-center rounded-md border border-gray-200" data-id="element-3321">
              <p className="text-gray-500" data-id="element-3322">
                No invoices linked to this credit note
              </p>
            </div>}
        </div>
        {/* Notes */}
        {creditNote.notes && <div className="mb-8" data-id="element-3323">
            <div className="flex items-center mb-4" data-id="element-3324">
              <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3325" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3326">Notes</h4>
            </div>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-3327">
              {renderNoteVisibility()}
              <p className="text-sm text-gray-700 mt-2" data-id="element-3328">{creditNote.notes}</p>
            </div>
          </div>}
        {/* Related Communications - New Section */}
        {relatedCommunications.length > 0 && <div className="mb-8" data-id="element-3329">
            <div className="flex items-center justify-between mb-4" data-id="element-3330">
              <div className="flex items-center" data-id="element-3331">
                <MessageSquareIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3332" />
                <h4 className="text-lg font-medium text-gray-900" data-id="element-3333">
                  Related Communications
                </h4>
              </div>
              <a href="#" className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center" data-id="element-3334">
                View All
                <ExternalLinkIcon className="h-4 w-4 ml-1" data-id="element-3335" />
              </a>
            </div>
            <div className="bg-gray-50 rounded-md border border-gray-200 divide-y divide-gray-200" data-id="element-3336">
              {relatedCommunications.map(comm => <div key={comm.id} className="p-4 hover:bg-gray-100" data-id="element-3337">
                  <div className="flex justify-between" data-id="element-3338">
                    <h5 className="text-sm font-medium text-indigo-600" data-id="element-3339">
                      {comm.title}
                    </h5>
                    <span className="text-xs text-gray-500" data-id="element-3340">
                      {new Date(comm.timestamp).toLocaleDateString(locale)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2" data-id="element-3341">
                    {comm.lastMessage}
                  </p>
                  <div className="mt-2 flex justify-between items-center" data-id="element-3342">
                    <span className="text-xs text-gray-500" data-id="element-3343">
                      {comm.messageCount}{' '}
                      {comm.messageCount === 1 ? 'message' : 'messages'}
                    </span>
                    <a href="#" className="text-xs text-indigo-600 hover:text-indigo-800" data-id="element-3344">
                      View Thread
                    </a>
                  </div>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}